import get from 'lodash/get';
import some from 'lodash/some';
import {
  ConfigOptionDef,
  PermissionDef,
  SubjectDef,
} from 'xm-constants/libraries';
import { getAllUsersEntity } from 'actions/permissionSearch/permissionSearch';
import { permissionSort } from 'selectors/scriptStep/permissions';
import { getSubjectDisplayName } from './permissions/permissions';

export const canEdit = library =>
  library && library.accessType === PermissionDef.canEdit;
export const canUse = library =>
  (library && canEdit(library)) || library.accessType === PermissionDef.canUse;

export const isCompanyAdmin = loggedInUser =>
  loggedInUser.loggedInUserProfile.roles
    ? some(loggedInUser.loggedInUserProfile.roles.data, {
        name: 'Company Admin',
      })
    : false;

export const isLibraryOwner = (library, loggedInUser) =>
  library.owner.id === loggedInUser.loggedInUserProfile.id;

// The Company Supervisor should have this permission.
export const hasPermissionReassignFlowLibraryOwner = loggedInUser =>
  some(loggedInUser.loggedInUserProfile.permissions, {
    id: 'ability.act.ReassignFlowLibraryOwner',
  });

export const supervisesLibraryOwner = (library, loggedInUserProfile) =>
  library.ownerSupervisors
    ? some(library.ownerSupervisors.data, {
        id: loggedInUserProfile.id,
      })
    : false;

export const canUseOrIsAuthorized = (library, loggedInUser) =>
  canUse(library) ||
  isCompanyAdmin(loggedInUser) ||
  hasPermissionReassignFlowLibraryOwner(loggedInUser) ||
  supervisesLibraryOwner(library, loggedInUser) ||
  get(library, 'owner.id') === get(loggedInUser, 'loggedInUserProfile.id');

export const makeLibraryPermissionEntry = (
  library,
  access,
  subject,
  subjectType = subject.recipientType,
) => ({
  displayName: getSubjectDisplayName(subject),
  library,
  shareType: access,
  subject: {
    id: subject.id,
  },
  subjectType,
});

export const fromApiSchema = (library, props) => {
  const permitted = get(library, 'permitted.data', []).map(entity =>
    makeLibraryPermissionEntry(
      library,
      entity.shareType,
      entity.subject,
      entity.subjectType,
    ),
  );

  let data = [
    makeLibraryPermissionEntry(library, PermissionDef.owner, library.owner),
    ...permitted,
  ];

  // Add an entry for *everyone* (accessibleByAll)
  if (library[ConfigOptionDef.accessibleByAll] !== PermissionDef.notShared) {
    data = [
      ...data,
      makeLibraryPermissionEntry(
        library,
        library.accessibleByAll,
        getAllUsersEntity(props),
      ),
    ];
  }

  return {
    library,
    owner: library.owner,
    permitted: data.sort(permissionSort),
  };
};

export function toOwnerApiSchema(formData) {
  const apiSchema = { id: formData.library.id };
  const owner = formData.permitted.find(
    p => p.shareType === PermissionDef.owner,
  );

  if (formData.owner.id !== owner.subject.id) {
    apiSchema.owner = formData.owner;
  }

  return apiSchema;
}

export function toApiSchema(formData) {
  const apiSchema = toOwnerApiSchema(formData);

  const allUserPermission = formData.permitted.find(
    p => p.subjectType === SubjectDef.allUsers,
  );

  apiSchema.accessibleByAll =
    allUserPermission !== undefined
      ? allUserPermission.shareType
      : PermissionDef.notShared;

  apiSchema.permitted = formData.permitted.filter(
    p =>
      p.shareType !== PermissionDef.owner &&
      p.subjectType !== SubjectDef.allUsers,
  );

  return apiSchema;
}

export { permissionSort };
